import Minilog from 'minilog';

import URI from 'urijs';
import * as Cookies from 'js-cookie';
import {SiteSettings} from './base';

let logger = Minilog('frontend.models.services');

export class HeaderData {
    static HEADER_CONTENT_TYPE = 'Content-Type';
    static HEADER_CONTENT_LENGTH = 'Content-Length';
    static CONTENT_TYPE_JSON = 'application/json';

    static hasContent(headers: Headers) {
        return headers.get(this.HEADER_CONTENT_LENGTH) !== '0';
    }

    static isJson(headers: Headers) {
        return headers.get(this.HEADER_CONTENT_TYPE) === this.CONTENT_TYPE_JSON;
    }

    static createJsonHeaders() {
        let headers = {};
        headers[this.HEADER_CONTENT_TYPE] = this.CONTENT_TYPE_JSON;
        return new Headers(headers);
    }
}

/**
 * Enumeration of common HTTP status codes and utility functions for them
 */
export class HttpStatusCodes {
    static CODE_200_OK = 200;
    static CODE_201_CREATED = 201;
    static CODE_204_NOCONTENT = 204;
    static CODE_205_RESETCONTENT = 205;
    static CODE_300_MULTIPLECHOICES = 300;
    static CODE_304_NOT_MODIFIED = 304;
    static CODE_400_BAD_REQUEST = 400;
    static CODE_401_UNAUTHENTICATED = 401;
    static CODE_403_FORBIDDEN = 403;
    static CODE_404_NOT_FOUND = 404;
    static CODE_500_SERVER_ERROR = 500;

    /**
     * Does the status code given represent a valid response?
     * @param code status code value
     */
    static isValid(code: HttpStatusCodes) {
        return code >= this.CODE_200_OK && code < this.CODE_300_MULTIPLECHOICES
            || code === this.CODE_304_NOT_MODIFIED;
    }

    static hasNoContent(code: HttpStatusCodes) {
        return (code === this.CODE_204_NOCONTENT
                || code === this.CODE_205_RESETCONTENT);
    }
}

/**
 * Enumeration of all the available request methods for the services
 */
class RequestMethod {
    static GET = 'GET';
    static POST = 'POST';
    static PUT = 'PUT';
    static PATCH = 'PATCH';
    static DELETE = 'DELETE';

    static isProxyUnfriendlyMethod(method: RequestMethod) {
        return method !== this.GET && method !== this.POST;
    }
}

/**
 * Possible arguments for the RestService constructor
 */
interface RestOptionalArgs {
    prefix?: string;
    baseUri?: string;
}

/**
 * Possible arguments for the RestService request function
 */
interface RequestOptionalArgs {
    params?: any;
    data?: any;
    uriPostfix?: string;
    resourceId?: any;
    fullUri?: string;
    willHandleResponse?: boolean;
    disableRedirect?: boolean;
}

/**
 * Common type declarations for all responses from a RestService request
 */
declare type RequestResponse<T> = Response | SuccessResponse<T> | FailedResponse<T> | void | any;
export declare type RequestPromise<T> = Promise<RequestResponse<T>>;

/**
 * Format of the successful response from any RestService calls.
 */
export interface SuccessResponse<T> {
    status_code: number;
    headers: Headers | {};
    data: T;
    error?: any;
}

/**
 * Format of the failed response from any RestService calls.
 */
export interface FailedResponse<T> {
    name: string;
    message: string;
    response: Response;
    data: T;
}

/**
 * Base class for all external REST services. Wraps common functionality,
 * such as error handling, JSON parsing, etc.
 */
export abstract class RestService {
    baseUri: string;
    prefix: string;
    resource: string;

    constructor(resource?: string, options?: RestOptionalArgs) {
        this.resource = resource || '';
        this.baseUri = options.baseUri || SiteSettings.uri.main;
        this.prefix = options.prefix || '';
    }

    /**
     * Constructs the request URI from the given arguments
     *
     * @param options
     * @returns {URI}
     */
    createUri(options?: RequestOptionalArgs) : any {
        let base = `${this.baseUri}${this.prefix}/${this.resource}/`;
        if (options.uriPostfix) {
            base += options.uriPostfix;
        }

        if (options.resourceId) {
            base += `${options.resourceId}/`;
        }

        return URI(base);
    }

    /**
     * Handler for reading all pages from a paginated API call.
     *
     * @param response
     * @param pagedData
     * @returns {RequestPromise<T>}
     */
    handlePageResponse<T>(response: Response, pagedData: Array<T>) : RequestPromise<T> {
        let obj = this;
        if (response.status === HttpStatusCodes.CODE_200_OK) {
            return response.json().then(function(json_data) {
                // store new page of results in accumulator
                pagedData.push(...json_data.results);
                // if the response has a valid next entry, continue paging
                if (json_data.next) {
                    return obj.request(RequestMethod.GET, {fullUri: json_data.next,
                                                           willHandleResponse: false})
                        .then(function(nextResponse) {
                            return obj.handlePageResponse(nextResponse, pagedData);
                        });
                }
                // if all results are paged, resolve now
                return Promise.resolve({
                    status_code: response.status,
                    headers: response.headers,
                    data: pagedData
                });
            });
        } else {
            // for errors and other unexpected responses, fall back to standard
            // handler
            return obj.handleResponse<T>(response);
        }
    }

    /**
     * Standard handler for request responses. Converts the raw response into
     * formatted data.
     *
     * @param response raw fetch response
     * @returns {RequestPromise<T>}
     */
    handleResponse<T>(response: Response) : RequestPromise<T> {
        logger.debug('Handling response', response);
        // no content so no data
        if (HttpStatusCodes.hasNoContent(response.status)) {
            return Promise.resolve({
                status_code: response.status,
                headers: response.headers,
                data: null
            });

        } else if (HttpStatusCodes.isValid(response.status)) {
            // annoyingly, json() returns Promise instead of plain obj
            return response.json().then(
                function(json_data) {
                    return Promise.resolve({
                        status_code: response.status,
                        headers: response.headers,
                        data: json_data
                    });
                },
                function() {
                    logger.warn(`Unable to convert ${response.statusText} body`);
                    return Promise.resolve({
                        status_code: response.status,
                        headers: response.headers,
                        data: null
                    });
                }
            );

        } else {
            // force error response if status does not have standard
            // HTTP code
            // parse as JSON if possible, otherwise read in text of response
            let headers = response.headers;
            if (!HeaderData.hasContent(headers)) {
                return Promise.reject({
                    name: 'JSONError',
                    message: response.statusText,
                    response: response,
                    data: null
                });
            } else {
                let responseBody = HeaderData.isJson(response.headers)
                    ? response.json() : response.text();
                return responseBody.then(
                    function(body) {
                        logger.info(`Failed to parse request with error ${response.statusText}
                             and response ${JSON.stringify(body)}`);
                        return Promise.reject({
                            name: 'JSONError',
                            message: response.statusText,
                            response: response,
                            data: body
                        });
                    },
                    function() {
                        logger.warn(`Unable to convert ${response.statusText} body`);
                        return Promise.reject({
                            name: 'JSONError',
                            message: response.statusText,
                            response: response,
                            data: null
                        });
                    }
                );
            }
        }
    }

    /**
     * Makes a request to the configured service with the given method and
     * options, returning a Promise that will ultimately output the response.
     *
     * @returns {RequestPromise<T>}
     */
    request<T>(method: string, options?: RequestOptionalArgs) : RequestPromise<T> {
        let uri = options.fullUri || this.createUri(options);
        let initData: any = {
            headers: new Headers(),
            // ensures Ajax call uses cookies; see
            // https://developer.mozilla.org/en-US/docs/Web/API/Request/credentials
            credentials: 'include',
            // see https://github.com/whatwg/fetch/issues/763 for why 'manual' == disabled
            redirect: options.disableRedirect ? 'manual' : 'follow',
            method
        };

        if (options && options.params) {
            uri.addSearch(options.params);
        }

        if (method !== RequestMethod.GET && options && options.data) {
            initData.body = JSON.stringify(options.data);
            initData.headers.append('Content-Type', 'application/json');
        }
        // pass through headers as appropriate
        initData.headers.append('Accept', 'application/json, */*');
        initData.headers.append('X-CSRFToken', Cookies.get('csrftoken'));
        // must workaround proxy issues
        if (RequestMethod.isProxyUnfriendlyMethod(method)) {
            initData.headers.append('X-HTTP-Method-Override', method);
            initData.method = RequestMethod.POST;
        }

        // allow for making request without always injecting response handling
        let call = window.fetch(uri.valueOf(), initData);
        let willHandleResponse =
            options.willHandleResponse === undefined ?
                true : options.willHandleResponse;
        if (willHandleResponse) {
            call = call.then(this.handleResponse);
        }
        return call;
    }

    /**
     * Lists the set of paginated results for the given parameters
     *
     * @param params
     * @returns {Promise<SuccessResponse<T>>}
     */
    list<T>(params?: any) : RequestPromise<T> {
        return this.request<T>(RequestMethod.GET, {params: params});
    }

    /**
     * Lists all results from a paginated endpoint, consecutively calling
     * the next page until all results are returned.
     *
     * @param params
     * @returns {Promise<SuccessResponse<T>>}
     */
    listAll<T>(params?: any) : RequestPromise<T> {
        let allParams = params || {};
        // ensure the page size is large enough so we don't make too many
        // needless calls
        allParams.page_size = 1000;
        let obj = this;
        return obj.request<T>(RequestMethod.GET, {params: allParams, willHandleResponse: false})
            .then(function(response: RequestResponse<T>) : RequestPromise<T> {
                let pagedData = [];
                return obj.handlePageResponse(response, pagedData);
            });
    }

    get<T>(resourceId?: any) : RequestPromise<T> {
        return this.request<T>(RequestMethod.GET, {resourceId});
    }

    create<T>(data: any, params?: any, options?: RequestOptionalArgs) : RequestPromise<T> {
        options = options || {};
        options.params = params;
        options.data = data;
        return this.request<T>(RequestMethod.POST, options);
    }

    update<T>(id: any, data: any, options?: RequestOptionalArgs) : RequestPromise<T> {
        options = options || {};
        options.resourceId = id;
        options.data = data;
        return this.request<T>(RequestMethod.PATCH, options);
    }

    delete<T>(id: any, params?: any, data?: any, options?: RequestOptionalArgs) : RequestPromise<T> {
        options = options || {};
        options.params = params;
        options.resourceId = id;
        options.data = data;
        return this.request<T>(RequestMethod.DELETE, options);
    }
}

export interface V1Meta {
    limit: number;
    next: string;
    offset: number;
    previous: string;
    total_count: number;
}

export interface V1ListModel<T> {
    meta: V1Meta;
    objects: T[];
}

export interface V2ListModel<T> {
    count: number;
    next: string;
    previous: string;
    results: T[];
}

/**
 * Basic class for handling country API calls
 */
export class V1Resource extends RestService {
    // all resource names supported
    static COUNTRY_APP = 'country';
    static REGION = {app: V1Resource.COUNTRY_APP, resource: 'region'};

    constructor({resource, app}) {
        super(resource, {prefix: `/${app}/api/v1`});
    }
}

/**
 * Basic class for handling all v2 API calls
 */
export class V2Resource extends RestService {
    // all resource names supported
    static PREMISES = 'premises';
    static ALARM = 'alarm';
    static GATEWAY = 'gateway';
    static UNIT = 'unit';

    constructor(resource: string) {
        super(resource, {prefix: '/console/api/v2'});
    }

    count(params?: any): Promise<any> {
        return this.request(RequestMethod.GET, {
            params: params, uriPostfix: 'count/'
        });
    }
}

/**
 * Basic class for handling all new v2 API calls
 */
export class NewV2Resource extends RestService {
    // all resource names supported
    static REGION = 'region';
    static COUNTRY = 'country';

    constructor(resource: string) {
        super(resource, {prefix: '/api/v2'});
    }
}

/**
 * Basic class for handling all v3 API calls
 */
export class V3Resource extends RestService {
    // all resource names supported
    static USER = 'user';
    static ALARM = 'alarm';
    static SESSION = 'session';
    static PREMISES = 'premises';

    constructor(resource: string) {
        super(resource, {prefix: '/api/v3'});
    }
}

/**
 * Basic class foe handling users API calls.
 */
export class V1UsersResource extends RestService {
    static TEXT = 'text';

    constructor(resource: string) {
        super(resource, {prefix: '/api/v1', baseUri: SiteSettings.uri.users});
    }

}

/**
 * Basic class for handling all Calibration Unit API calls
 */
export class CalibrationUnitResource extends RestService {
    static ASSIGNED = 'assigned';

    constructor() {
        super('calibrationunit', {prefix: '/api/v1', baseUri: SiteSettings.uri.calibration});
    }
}


/**
 * Basic class for handling all Calibration Status API calls
 */
export class CalibrationStatusResource extends RestService {
    constructor() {
        super('calibrationstatus', {prefix: '/api/v1', baseUri: SiteSettings.uri.calibration});
    }

    stop(params?: any) : Promise<any> {
        return this.request(RequestMethod.POST, {
            params: params, uriPostfix: 'stop'
        });
    }
}

/**
 * Basic class to query titan.
 */
export class Readings extends RestService {
    constructor() {
        super('readings', {prefix: '/v2', baseUri: SiteSettings.uri.readings});
    }
}
